import { forwardRef } from "react";
import { NativeSelect, NativeSelectProps, OutlinedInput, SxProps, Theme } from "@mui/material";
import { IconCaret } from "src/components/icons-mui/caret";

export interface IWfSelectProps extends NativeSelectProps {
    placeholder?: string;
    inputSx?: SxProps<Theme>;
}

const CustomIconCaret = () => (
    <IconCaret
        sx={{
            width: "24px",
            height: "24px",
            position: "absolute",
            right: "16px",
            pointerEvents: "none",
            userSelect: "none",
        }}
    />
);

const isNativeSelectAttribute = (key: string) => key.startsWith("data-") || key.startsWith("aria-") || key === "id" || key === "name";

export const WfNativeSelect = forwardRef<HTMLSelectElement, IWfSelectProps>(
    ({ children, sx, inputSx, value, defaultValue, placeholder, inputProps, ...selectProps }, ref) => {
        const isPlaceholderSelected = value === "";

        let uncontrolledDefaultValue: typeof defaultValue;
        if (value === undefined) {
            uncontrolledDefaultValue = defaultValue ?? (placeholder ? "" : undefined);
        }

        const nativeSelectAttributes: Record<string, unknown> = {};
        const rootProps: Record<string, unknown> = {};
        Object.entries(selectProps).forEach(([key, propValue]) => {
            if (isNativeSelectAttribute(key)) {
                nativeSelectAttributes[key] = propValue;
            } else {
                rootProps[key] = propValue;
            }
        });

        return (
            <NativeSelect
                input={
                    <OutlinedInput
                        slotProps={{
                            input: {
                                sx: [
                                    {
                                        boxSizing: "border-box",
                                        position: "relative",
                                        lineHeight: 1.5,
                                        paddingRight: theme => `${theme.spacing(6)} !important`,
                                        backgroundColor: "common.white",
                                        borderRadius: theme => `${theme.shape.borderRadius}px`,
                                        color: isPlaceholderSelected ? "gray.500" : "inherit",
                                    },
                                    ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
                                ],
                            },
                        }}
                    />
                }
                ref={ref}
                value={value}
                defaultValue={uncontrolledDefaultValue}
                inputProps={{ ...nativeSelectAttributes, ...inputProps }}
                sx={[
                    {
                        colorScheme: "light",
                        "& .MuiNativeSelect-select:not([multiple]) option, & .MuiNativeSelect-select:not([multiple]) optgroup": {
                            backgroundColor: "var(--mui-palette-common-white) !important",
                            color: "var(--mui-palette-gray-900) !important",
                        },
                        "& .MuiNativeSelect-select:not([multiple]) option[value='']": {
                            backgroundColor: "var(--mui-palette-common-white) !important",
                            color: "var(--mui-palette-gray-500) !important",
                        },
                        "& .MuiNativeSelect-select:not([multiple]) option:disabled": {
                            backgroundColor: "var(--mui-palette-common-white) !important",
                            color: "var(--mui-palette-gray-400) !important",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none !important",
                            boxShadow: "none !important",
                        },
                        "& .MuiNativeSelect-select": {
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "gray.400",
                            outline: "none",
                            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                        },
                        "& .MuiNativeSelect-select:focus": {
                            borderRadius: theme => `${theme.shape.borderRadius}px !important`,
                        },
                        "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiNativeSelect-select": {
                            borderColor: "gray.800",
                        },
                        "&.Mui-focused .MuiNativeSelect-select": {
                            borderColor: "gray.800",
                            boxShadow: "0 0 0 1px var(--mui-palette-gray-800)",
                        },
                        "&.Mui-disabled .MuiNativeSelect-select": {
                            borderColor: "gray.400",
                        },
                        "&.Mui-error .MuiNativeSelect-select": {
                            borderColor: "red.700",
                        },
                        "&.Mui-focused.Mui-error .MuiNativeSelect-select": {
                            boxShadow: "0 0 0 1px var(--mui-palette-red-700)",
                        },
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                IconComponent={CustomIconCaret}
                {...rootProps}
            >
                {placeholder && <option value="">{placeholder}</option>}
                {children}
            </NativeSelect>
        );
    }
);

WfNativeSelect.displayName = "WfNativeSelect";
